/**
 * main-product-v4.js
 *
 * KEY FEATURE: Flavor selector uses actual product variants.
 * Clicking a flavor card:
 *   1. Marks it as selected
 *   2. Finds the matching variant from embedded product JSON
 *      (matches by flavor value; also considers audience option value
 *       if the audience block carries a data-option-value attribute)
 *   3. Updates all price displays + the hidden variant input
 *
 * Other handlers: audience selector, sub/OTP toggle,
 * FAQ accordion, nutritional label dialog, gift dialogs.
 */

(function () {
  var scriptTag = document.currentScript;
  var sectionId = scriptTag ? scriptTag.dataset.id : null;

  function getSection() {
    return sectionId ? document.getElementById(sectionId) : document;
  }

  /* ── Helpers ── */

  function fmtMoney(cents) {
    if (cents == null) return '';
    var val = cents / 100;
    return '$' + (val % 1 === 0 ? val.toFixed(0) : val.toFixed(2));
  }

  /* ── Gallery: Swiper init + variant switching ── */

  function initGallerySwiper(section) {
    if (typeof Swiper === 'undefined') return;
    section.querySelectorAll('.productVisualSwiperGroupItem').forEach(function (el) {
      var swiperEl = el.querySelector('.productVisualSwiper');
      if (!swiperEl) return;

      var instance = new Swiper(swiperEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        autoHeight: true,
        pagination: {
          el: swiperEl.querySelector('.swiper-pagination'),
          clickable: true,
        },
        on: {
          slideChange: function () {
            var thumbs = el.querySelectorAll('.productVisualThumbsSlide');
            thumbs.forEach(function (t) { t.classList.remove('active'); });
            if (thumbs[this.realIndex]) thumbs[this.realIndex].classList.add('active');
          }
        }
      });

      // Thumbnail click → slide to that index
      el.querySelectorAll('.productVisualThumbsSlide').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          instance.slideTo(parseInt(this.dataset.index, 10) - 1);
        });
      });
    });
  }

  function switchGallery(section, id) {
    section.querySelectorAll('.productVisualSwiperGroupItem').forEach(function (el) {
      el.classList.toggle('active', el.dataset.id === id);
    });
  }

  function parseProductJSON(section) {
    var el = section.querySelector('.productV4JSON');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  /* ── Variant resolution ── */

  /**
   * Given the selected flavor value (string) and an optional audience
   * option value, find the best-matching variant from product data.
   */
  function resolveVariant(productData, flavorValue, audienceValue) {
    if (!productData || !productData.variants) return null;
    var variants = productData.variants;

    // Try: audience option match + flavor match
    if (audienceValue) {
      var match = variants.find(function (v) {
        return v.options.indexOf(audienceValue) !== -1 &&
               v.options.indexOf(flavorValue) !== -1;
      });
      if (match) return match;
    }

    // Fall back: flavor match only (first variant with that flavor value)
    return variants.find(function (v) {
      return v.options.indexOf(flavorValue) !== -1;
    }) || null;
  }

  /**
   * Apply a resolved variant to the section: update prices,
   * hidden input, and availability state.
   */
  function applyVariant(section, variant, productData) {
    if (!variant) return;

    // ── Gallery: show images for this variant ──
    switchGallery(section, String(variant.id));

    // ── Variant input (for cart) ──
    var variantInput = section.querySelector('.productV4VariantInput');
    if (variantInput) variantInput.value = variant.id;

    // ── Prices: calculate dynamically from buy group percent (mirrors v2 updateProduct) ──
    var activePlan = section.querySelector('.productV4GroupsItemInput:checked');
    var percent    = activePlan ? (parseInt(activePlan.dataset.percent, 10) || 0) : 0;
    var quantity   = activePlan ? (parseInt(activePlan.dataset.quantity, 10) || 1) : 1;

    var otpPrice   = variant.price * quantity;
    var subPrice   = Math.round(variant.price * (1 - percent / 100)) * quantity;
    var cmpPrice   = variant.compare_at_price;
    var isSub      = section.querySelector('.productV4PurchaseRadio[value="subscription"]:checked') !== null;
    var displayPrice = isSub ? subPrice : otpPrice;

    // Main price display — both amount and cadence come from the buy_group's
    // "monthly" setting (e.g. "$39.99/monthly", "$79.98/8 weeks"). Splitting on
    // "/" gives [amount, cadence]. Hidden on OTP or when no plan exists.
    var monthlyParts = (isSub && activePlan && activePlan.dataset.monthly)
      ? activePlan.dataset.monthly.split('/')
      : null;
    var mainPriceEl = section.querySelector('.productV4Price');
    if (mainPriceEl) {
      mainPriceEl.textContent = monthlyParts
        ? monthlyParts[0].trim()
        : fmtMoney(displayPrice);
    }
    var pricePerEl = section.querySelector('.productV4PricePer');
    if (pricePerEl) {
      var perText = monthlyParts && monthlyParts[1] ? monthlyParts[1].trim() : '';
      if (perText) {
        pricePerEl.textContent = '/' + perText;
        pricePerEl.style.display = '';
      } else {
        pricePerEl.style.display = 'none';
      }
    }

    // Compare-at price
    var comparePriceEl = section.querySelector('.productV4ComparePrice');
    if (comparePriceEl) {
      if (cmpPrice && cmpPrice > variant.price) {
        comparePriceEl.textContent = fmtMoney(cmpPrice);
        comparePriceEl.style.display = '';
      } else {
        comparePriceEl.style.display = 'none';
      }
    }

    // Sub row price — left side from buy_group's "Monthly price text" (split on "/"),
    // right side overridable per buy_group via the "Subscription cadence label" setting
    // (inline_richtext, exposed as data-sub-price-per). Falls back to the suffix from
    // the monthly text when the override is empty.
    var subPriceEl = section.querySelector('.productV4SubPrice');
    if (subPriceEl) {
      if (activePlan && activePlan.dataset.monthly) {
        var subParts = activePlan.dataset.monthly.split('/');
        var subAmount = subParts[0].trim();
        var subPerFallback = subParts[1] ? subParts[1].trim() : '';
        var subPerOverride = (activePlan.dataset.subPricePer || '').trim();
        var subPer = subPerOverride || subPerFallback;
        subPriceEl.textContent = subAmount;
        if (subPer) subPriceEl.insertAdjacentHTML('beforeend', '/' + subPer);
      } else {
        subPriceEl.textContent = fmtMoney(subPrice);
      }
    }

    // Sub box header discount badge — reflect selected plan's percent
    var discountEl = section.querySelector('.productV4SubBoxHeaderDiscount');
    if (discountEl && activePlan && activePlan.dataset.percent) {
      discountEl.textContent = 'SAVE ' + activePlan.dataset.percent + '%';
    }

    // OTP price (inside sub box) — prefer the buy group's otp_price text if set
    var otpPriceEl = section.querySelector('.productV4OtpPrice');
    if (otpPriceEl) {
      otpPriceEl.textContent = (activePlan && activePlan.dataset.otpPrice)
        ? activePlan.dataset.otpPrice
        : fmtMoney(otpPrice);
    }

    // ATC button price
    var atcPriceEl = section.querySelector('.productV4SubAtcPrice');
    if (atcPriceEl) atcPriceEl.textContent = fmtMoney(subPrice);

    // ATC availability
    var atcBtn = section.querySelector('.productV4Atc');
    if (atcBtn) {
      var available = variant.available !== false;
      atcBtn.disabled = !available;
      var atcTextEl = atcBtn.querySelector('.productV4AtcText');
      if (atcTextEl) {
        atcTextEl.textContent = available ? 'Add to Cart' : 'Sold Out';
      }
    }

    // Update variant JSON cache + URL
    var variantJsonEl = section.querySelector('.productV4VariantJSON');
    if (variantJsonEl) variantJsonEl.textContent = JSON.stringify(variant);
    var url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    // ── Selling plan availability: disable plans the variant doesn't support ──
    var availablePlanIds = (variant.selling_plan_allocations || []).map(function (a) {
      return String(a.selling_plan_id);
    });
    if (availablePlanIds.length > 0) {
      var needsReselect = false;
      section.querySelectorAll('.productV4GroupsItemInput').forEach(function (input) {
        var isAvailable = availablePlanIds.indexOf(input.value) !== -1;
        input.disabled = !isAvailable;
        var item = input.closest('.productV4GroupsItem');
        if (item) item.classList.toggle('is-disabled', !isAvailable);
        if (!isAvailable && input.checked) {
          input.checked = false;
          needsReselect = true;
        }
      });
      if (needsReselect) {
        var firstAvailable = section.querySelector('.productV4GroupsItemInput:not([disabled])');
        if (firstAvailable) {
          firstAvailable.checked = true;
          firstAvailable.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    // Plan-derived texts (servings, delivery, badge, save badge) — keep in sync
    applyPlanTexts(section);
  }

  /* ── Apply plan text fields (no variant needed) ── */

  function applyPlanTexts(section) {
    var activePlan = section.querySelector('.productV4GroupsItemInput:checked');
    if (!activePlan) return;

    // Sub price — amount from "Monthly price text" (split on "/"), suffix overridable
    // per buy_group via the "Subscription cadence label" inline_richtext setting.
    var subPriceEl = section.querySelector('.productV4SubPrice');
    if (subPriceEl && activePlan.dataset.monthly) {
      var subParts = activePlan.dataset.monthly.split('/');
      var subAmount = subParts[0].trim();
      var subPerFallback = subParts[1] ? subParts[1].trim() : '';
      var subPerOverride = (activePlan.dataset.subPricePer || '').trim();
      var subPer = subPerOverride || subPerFallback;
      subPriceEl.textContent = subAmount;
      if (subPer) subPriceEl.insertAdjacentHTML('beforeend', '/' + subPer);
    }

    // Sub box header discount
    var discountEl = section.querySelector('.productV4SubBoxHeaderDiscount');
    if (discountEl && activePlan.dataset.percent) {
      discountEl.textContent = 'SAVE ' + activePlan.dataset.percent + '%';
    }

    // Save badge
    var saveBadge = section.querySelector('.productV4SaveBadge');
    if (saveBadge && activePlan.dataset.percent) {
      saveBadge.textContent = 'SAVE ' + activePlan.dataset.percent + '%';
    }

    // OTP price from buy group's otp_price text field
    var otpPriceEl = section.querySelector('.productV4OtpPrice');
    if (otpPriceEl && activePlan && activePlan.dataset.otpPrice) {
      otpPriceEl.textContent = activePlan.dataset.otpPrice;
    }

    // Main price — both amount and cadence come from the buy_group's "monthly"
    // setting (e.g. "$39.99/monthly"). Splitting on "/" gives [amount, cadence].
    // On OTP, amount falls back to otp_price and cadence is hidden.
    var isSub = section.querySelector('.productV4PurchaseRadio[value="subscription"]:checked') !== null;
    var monthlyParts = (isSub && activePlan.dataset.monthly)
      ? activePlan.dataset.monthly.split('/')
      : null;
    var mainPriceEl = section.querySelector('.productV4Price');
    if (mainPriceEl) {
      if (monthlyParts) {
        mainPriceEl.textContent = monthlyParts[0].trim();
      } else if (!isSub && activePlan.dataset.otpPrice) {
        mainPriceEl.textContent = activePlan.dataset.otpPrice;
      }
    }
    var pricePerEl = section.querySelector('.productV4PricePer');
    if (pricePerEl) {
      var perText = monthlyParts && monthlyParts[1] ? monthlyParts[1].trim() : '';
      if (perText) {
        pricePerEl.textContent = '/' + perText;
        pricePerEl.style.display = '';
      } else {
        pricePerEl.style.display = 'none';
      }
    }

    // Delivery + servings from individual data attributes (robust to <br>, % chars)
    var deliveryEl = section.querySelector('.productV4SubRowDelivery');
    if (deliveryEl && activePlan.dataset.delivered) {
      deliveryEl.innerHTML = activePlan.dataset.delivered;
    }
    var servingsEl = section.querySelector('.productV4SubServings');
    if (servingsEl && activePlan.dataset.servingsDescription) {
      servingsEl.innerHTML = activePlan.dataset.servingsDescription;
    }

    // Price badge — "As low as $X/serving a day" — derived from active plan's servings_per
    var badgeEl = section.querySelector('.productV4PriceBadge');
    if (badgeEl && activePlan.dataset.servingsPer) {
      badgeEl.textContent = 'As low as ' + activePlan.dataset.servingsPer + ' a day';
    }
  }

  /* ── Update from current selections ── */

  function updateFromSelections(section) {
    var productData = parseProductJSON(section);
    if (!productData) return;

    var flavorCard = section.querySelector('.productV4FlavorCard.is-selected');
    var flavorValue = flavorCard ? (flavorCard.dataset.flavorValue || '') : '';

    // Variety pack: no single variant — update plan text fields only
    if (flavorValue === 'variety') {
      applyPlanTexts(section);
      return;
    }

    var variant;

    if (flavorCard) {
      // Flavor picker is present — resolve variant from card + audience
      var audienceInput = section.querySelector('.productV4AudienceInput:checked');
      var audienceValue = audienceInput ? (audienceInput.dataset.optionValue || '') : '';

      // Use data-variant-id from card directly when no audience filter applies
      var directVariantId = parseInt(flavorCard.dataset.variantId, 10);
      if (directVariantId && !audienceValue) {
        variant = productData.variants.find(function (v) { return v.id === directVariantId; });
      }
      if (!variant) {
        variant = resolveVariant(productData, flavorValue, audienceValue);
      }
    } else {
      // No flavor picker — resolve from the hidden variant input, or first available
      var variantInput = section.querySelector('.productV4VariantInput');
      var currentId = variantInput ? parseInt(variantInput.value, 10) : null;
      if (currentId) {
        variant = productData.variants.find(function (v) { return v.id === currentId; });
      }
      if (!variant) {
        variant = productData.variants.find(function (v) { return v.available; }) || productData.variants[0];
      }
    }

    applyVariant(section, variant, productData);
  }

  /* ── Audience selector ── */

  function initAudienceSelector(section) {
    var inputs = section.querySelectorAll('.productV4AudienceInput');
    inputs.forEach(function (input) {
      input.addEventListener('change', function () {
        section.querySelectorAll('.productV4AudienceItem').forEach(function (item) {
          item.classList.remove('is-selected');
        });
        var label = input.closest('.productV4AudienceItem');
        if (label) label.classList.add('is-selected');
        updateFromSelections(section);
      });
    });
  }

  /* ── Variety pack: restrict buy group plans ── */

  function applyVarietyPlanGating(section, varietyPlanId) {
    var inputs = section.querySelectorAll('.productV4GroupsItemInput');
    if (!varietyPlanId) return;

    // ── Gallery: show variety pack images ──
    switchGallery(section, 'variety');

    var needsReselect = false;
    inputs.forEach(function (input) {
      var isAllowed = input.value === varietyPlanId;
      input.disabled = !isAllowed;
      var item = input.closest('.productV4GroupsItem');
      if (item) item.classList.toggle('is-disabled', !isAllowed);
      if (!isAllowed && input.checked) {
        input.checked = false;
        needsReselect = true;
      }
    });

    if (needsReselect) {
      var allowed = section.querySelector('.productV4GroupsItemInput[value="' + varietyPlanId + '"]');
      if (allowed) {
        allowed.checked = true;
        allowed.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else {
      // Families plan was already selected — no change event fires, update texts directly
      applyPlanTexts(section);
    }
  }

  function clearVarietyPlanGating(section) {
    section.querySelectorAll('.productV4GroupsItemInput').forEach(function (input) {
      input.disabled = false;
      var item = input.closest('.productV4GroupsItem');
      if (item) item.classList.remove('is-disabled');
    });
  }

  /* ── Flavor selector (variant-based) ── */

  function initFlavorSelector(section) {
    var cards = section.querySelectorAll('.productV4FlavorCard');
    cards.forEach(function (card) {
      card.addEventListener('click', function () {
        cards.forEach(function (c) { c.classList.remove('is-selected'); });
        card.classList.add('is-selected');

        if (card.dataset.flavorValue === 'variety') {
          applyVarietyPlanGating(section, card.dataset.varietyPlan || '');
        } else {
          clearVarietyPlanGating(section);
          updateFromSelections(section);
        }
      });
    });
  }

  /* ── Sub / OTP purchase toggle ── */

  function initPurchaseToggle(section) {
    var radios     = section.querySelectorAll('.productV4PurchaseRadio');
    var subDetails = section.querySelector('.productV4SubDetails');
    var atcBtn     = section.querySelector('.productV4Atc');

    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        // Row highlight
        section.querySelectorAll('.productV4PurchaseRow').forEach(function (row) {
          row.classList.remove('is-selected');
        });
        var row = radio.closest('.productV4PurchaseRow');
        if (row) row.classList.add('is-selected');

        var isSub = radio.value === 'subscription';

        // Swap ATC price display
        var subAtcPrice = section.querySelector('.productV4SubAtcPrice');
        var otpAtcPrice = section.querySelector('.productV4OtpAtcPrice');
        if (subAtcPrice) subAtcPrice.style.display = isSub ? '' : 'none';
        if (otpAtcPrice) otpAtcPrice.style.display = isSub ? 'none' : '';

        // Recalculate prices for the toggled purchase type
        updateFromSelections(section);
      });
    });
  }

  /* ── FAQ accordion ── */

  function initFaqAccordion(section) {
    var btns = section.querySelectorAll('.productV4FaqBtn');
    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var isActive = btn.classList.contains('is-active');
        btns.forEach(function (b) { b.classList.remove('is-active'); });
        if (!isActive) btn.classList.add('is-active');
      });
    });
  }

  /* ── Nutritional label dialog ── */

  function initLabelDialog(section) {
    var openBtn = section.querySelector('.productV4LabelBtn');
    var dialog  = section.querySelector('.productV4LabelDialog');
    if (!openBtn || !dialog) return;

    // Init Swiper inside the dialog
    var labelSwiper = null;
    if (typeof Swiper !== 'undefined') {
      var swiperEl = dialog.querySelector('.productV4LabelSwiper');
      if (swiperEl) {
        labelSwiper = new Swiper(swiperEl, {
          slidesPerView: 1,
          spaceBetween: 20,
          autoHeight: true,
          navigation: {
            prevEl: dialog.querySelector('.productV4LabelNavBtn.swiper-button-prev'),
            nextEl: dialog.querySelector('.productV4LabelNavBtn.swiper-button-next'),
          },
        });
      }
    }

    // Open: jump to the slide matching the active variant
    openBtn.addEventListener('click', function () {
      if (labelSwiper) {
        var variantInput = section.querySelector('.productV4VariantInput');
        var currentId    = variantInput ? variantInput.value : null;
        if (currentId) {
          var slides = Array.from(dialog.querySelectorAll('.swiper-slide[data-id]'));
          var idx    = slides.findIndex(function (s) { return s.dataset.id === String(currentId); });
          if (idx >= 0) labelSwiper.slideTo(idx, 0);
        }
      }
      document.body.classList.add('noScroll');
      dialog.showModal();
    });

    var closeBtn = dialog.querySelector('.productV4LabelDialogClose');
    if (closeBtn) closeBtn.addEventListener('click', function () {
      dialog.close();
      document.body.classList.remove('noScroll');
    });

    dialog.addEventListener('click', function (e) {
      if (e.target === dialog) {
        dialog.close();
        document.body.classList.remove('noScroll');
      }
    });

    dialog.addEventListener('close', function () {
      document.body.classList.remove('noScroll');
    });
  }

  /* ── Buy group selector ── */

  function initBuyGroupSelector(section) {
    section.querySelectorAll('.productV4GroupsItemInput').forEach(function (input) {
      input.addEventListener('change', function () {
        // applyPlanTexts (called via updateFromSelections → applyVariant) handles
        // all plan-derived text updates (servings, delivery, badge, save badge).
        updateFromSelections(section);
      });
    });
  }

  /* ── ATC button ── */

  function initAtcButton(section, productData) {
    var atcBtn = section.querySelector('.productV4AtcSub');
    if (!atcBtn) return;

    atcBtn.addEventListener('click', async function () {
      this.classList.add('loading');

      var hasSub     = section.querySelector('.productV4PurchaseRadio[value="subscription"]:checked') !== null;
      var activePlan = section.querySelector('.productV4GroupsItemInput:checked');
      var planId     = activePlan ? (parseInt(activePlan.value) || null) : null;
      var subPlanId  = hasSub ? planId : null;
      var quantity   = activePlan ? (parseInt(activePlan.dataset.quantity) || 1) : 1;

      var variantInput = section.querySelector('.productV4VariantInput');
      var variantId    = variantInput ? parseInt(variantInput.value) : null;

      var flavorCard = section.querySelector('.productV4FlavorCard.is-selected');
      var isVariety  = flavorCard && flavorCard.dataset.flavorValue === 'variety';

      var formData = [];

      // Gifts (uses giftsV4 global set in Liquid)
      if (typeof giftsV4 !== 'undefined') {
        giftsV4.forEach(function (gift) {
          var giftId = parseInt(gift.id);
          if (!giftId) return;
          if (hasSub || !gift.sub) {
            formData.push({ quantity: 1, id: giftId, properties: { _gift: true } });
          }
        });
      }

      if (isVariety) {
        // Variety: add each real flavor variant individually (mirrors v2)
        section.querySelectorAll('.productV4FlavorCard:not(.productV4FlavorCardVariety)').forEach(function (card) {
          var vid = parseInt(card.dataset.variantId);
          if (!vid) return;
          formData.push({
            quantity: 1,
            id: vid,
            selling_plan: subPlanId,
            properties: { _plan: planId, _variety: true }
          });
        });
      } else {
        formData.push({
          quantity: quantity,
          id: variantId,
          selling_plan: subPlanId,
          properties: { _plan: planId }
        });
      }

      await fetch('/cart/clear.js');
      await addProduct({ items: formData });

      this.classList.remove('loading');
      await openCart();
    });
  }

  /* ── Gift / Learn more dialogs ── */

  function initGiftDialogs(section) {
    section.querySelectorAll('.productV4SubPointLearnBtn').forEach(function (btn) {
      var item   = btn.closest('.productV4SubPointItem');
      var dialog = item ? item.querySelector('.productV4SubPointLearnDialog') : null;
      if (!dialog) return;

      btn.addEventListener('click', function () { dialog.showModal(); });

      dialog.querySelectorAll('.productV4SubPointLearnClose').forEach(function (cb) {
        cb.addEventListener('click', function () { dialog.close(); });
      });

      dialog.addEventListener('click', function (e) {
        if (e.target === dialog) dialog.close();
      });
    });
  }

  /* ── Theme editor support ── */

  document.addEventListener('shopify:section:load', function (e) {
    if (e.detail && e.detail.sectionId === sectionId) init();
  });

  /* ── Boot ── */

  function init() {
    var section = getSection();
    if (!section) return;

    var productData = parseProductJSON(section);

    initGallerySwiper(section);
    initAudienceSelector(section);
    initFlavorSelector(section);
    initPurchaseToggle(section);
    initBuyGroupSelector(section);
    initAtcButton(section, productData);
    initFaqAccordion(section);
    initLabelDialog(section);
    initGiftDialogs(section);

    // Sync prices on initial load from the first selected flavor
    var initialVarietyCard = section.querySelector('.productV4FlavorCard.is-selected.productV4FlavorCardVariety');
    if (initialVarietyCard) {
      applyVarietyPlanGating(section, initialVarietyCard.dataset.varietyPlan || '');
    } else {
      updateFromSelections(section);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
